import React, { useState, useEffect } from 'react';
import Modal from '../../components/Modal';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { IconButton } from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import './style.css';

interface TimeSlot {
  time: string;
  status: 'available' | 'unavailable' | 'break';
}

interface WorkingHours {
  monday: { slots: TimeSlot[]; closed: boolean };
  tuesday: { slots: TimeSlot[]; closed: boolean };
  wednesday: { slots: TimeSlot[]; closed: boolean };
  thursday: { slots: TimeSlot[]; closed: boolean };
  friday: { slots: TimeSlot[]; closed: boolean };
  saturday: { slots: TimeSlot[]; closed: boolean };
  sunday: { slots: TimeSlot[]; closed: boolean };
}

interface WorkingHoursModalProps {
  isOpen: boolean;
  onClose: () => void;
  masterId: string;
  masterName: string;
  appointmentId?: string;
  isReschedule?: boolean;
  onRescheduleSuccess?: () => void;
}

const WorkingHoursModal: React.FC<WorkingHoursModalProps> = ({
  isOpen,
  onClose,
  masterId,
  masterName,
  appointmentId,
  isReschedule = false,
  onRescheduleSuccess,
}) => {
  const { language } = useLanguage();
  const { addNotification } = useNotification();
  const { user } = useAuth();
  const [workingHours, setWorkingHours] = useState<WorkingHours | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [weekOffset, setWeekOffset] = useState(0);

  // Set current day as default (0 = Sunday, 1 = Monday, etc. in JS, but we want Monday = 0)
  const getCurrentDayIndex = () => {
    const today = new Date().getDay(); // 0 = Sunday, 1 = Monday, etc.
    return today === 0 ? 6 : today - 1; // Convert to our array index (Monday = 0)
  };

  const [currentDayIndex, setCurrentDayIndex] = useState(getCurrentDayIndex());

  const isToday = (dayIndex: number, weekOffset: number) => {
    const today = new Date();
    const targetDate = new Date(today);

    // Calculate the target date for the selected day
    // dayIndex: 0 = Monday, 1 = Tuesday, ..., 6 = Sunday
    // weekOffset: 0 = current week, 1 = next week, -1 = previous week
    const currentDayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.

    // Convert to Monday-based index (0 = Monday, 6 = Sunday)
    const currentMondayIndex = currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1;

    // Calculate days to add to reach the target day
    let daysToAdd = dayIndex - currentMondayIndex + (weekOffset * 7);

    targetDate.setDate(today.getDate() + daysToAdd);

    return targetDate.toDateString() === today.toDateString();
  };

  const [bookingConfirmation, setBookingConfirmation] = useState<{
    isOpen: boolean;
    day: string;
    time: string;
    date: string;
    notes?: string;
  } | null>(null);

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  useEffect(() => {
    if (isOpen && masterId) {
      setWeekOffset(0); // Reset to current week when modal opens
      fetchWorkingHours();
    }
  }, [isOpen, masterId]);

  useEffect(() => {
    if (isOpen && masterId) {
      fetchWorkingHours();
    }
  }, [weekOffset]); // Re-fetch when week offset changes

  const fetchWorkingHours = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No auth token');
      }

      const response = await fetch(`/api/v1/working-hours/${masterId}/available?weekOffset=${weekOffset}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Convert API response format to component format
      const convertedWorkingHours: WorkingHours = {
        monday: {
          closed: data.monday?.length === 0,
          slots: (data.monday || []).map((slot: any) => ({
            time: slot.start_time,
            status: slot.status
          }))
        },
        tuesday: {
          closed: data.tuesday?.length === 0,
          slots: (data.tuesday || []).map((slot: any) => ({
            time: slot.start_time,
            status: slot.status
          }))
        },
        wednesday: {
          closed: data.wednesday?.length === 0,
          slots: (data.wednesday || []).map((slot: any) => ({
            time: slot.start_time,
            status: slot.status
          }))
        },
        thursday: {
          closed: data.thursday?.length === 0,
          slots: (data.thursday || []).map((slot: any) => ({
            time: slot.start_time,
            status: slot.status
          }))
        },
        friday: {
          closed: data.friday?.length === 0,
          slots: (data.friday || []).map((slot: any) => ({
            time: slot.start_time,
            status: slot.status
          }))
        },
        saturday: {
          closed: data.saturday?.length === 0,
          slots: (data.saturday || []).map((slot: any) => ({
            time: slot.start_time,
            status: slot.status
          }))
        },
        sunday: {
          closed: data.sunday?.length === 0,
          slots: (data.sunday || []).map((slot: any) => ({
            time: slot.start_time,
            status: slot.status
          }))
        },
      };

      setWorkingHours(convertedWorkingHours);
    } catch (err) {
      console.error('Error fetching working hours:', err);
      setError(language === 'ua' ? 'Не вдалося завантажити години роботи' : 'Failed to load working hours');
    } finally {
      setLoading(false);
    }
  };

  const formatDayName = (day: string) => {
    const days = {
      monday: language === 'ua' ? 'Понеділок' : 'Monday',
      tuesday: language === 'ua' ? 'Вівторок' : 'Tuesday',
      wednesday: language === 'ua' ? 'Середа' : 'Wednesday',
      thursday: language === 'ua' ? 'Четвер' : 'Thursday',
      friday: language === 'ua' ? 'П\'ятниця' : 'Friday',
      saturday: language === 'ua' ? 'Субота' : 'Saturday',
      sunday: language === 'ua' ? 'Неділя' : 'Sunday',
    };
    return days[day as keyof typeof days] || day;
  };

  const goToPreviousDay = () => {
    if (currentDayIndex > 0) {
      setCurrentDayIndex(currentDayIndex - 1);
    } else {
      // Go to previous week
      setWeekOffset(prev => prev - 1);
      setCurrentDayIndex(days.length - 1);
    }
  };

  const goToNextDay = () => {
    if (currentDayIndex < days.length - 1) {
      setCurrentDayIndex(currentDayIndex + 1);
    } else {
      // Go to next week
      setWeekOffset(prev => prev + 1);
      setCurrentDayIndex(0);
    }
  };

  // Calculate target date for a given day index and week offset
  const calculateTargetDate = (dayIndex: number, weekOffset: number = 0) => {
    const today = new Date();
    const targetDate = new Date(today);

    // Calculate the target date for the selected day
    // dayIndex: 0 = Monday, 1 = Tuesday, ..., 6 = Sunday
    // weekOffset: 0 = current week, 1 = next week, -1 = previous week
    const currentDayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.

    // Convert to Monday-based index (0 = Monday, 6 = Sunday)
    const currentMondayIndex = currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1;

    // Calculate days to add to reach the target day
    let daysToAdd = dayIndex - currentMondayIndex + (weekOffset * 7);

    targetDate.setDate(today.getDate() + daysToAdd);
    return targetDate;
  };

  const getFormattedDate = (dayOffset: number, weekOffset: number = 0) => {
    const targetDate = calculateTargetDate(dayOffset, weekOffset);

    const day = targetDate.getDate().toString().padStart(2, '0');
    const month = (targetDate.getMonth() + 1).toString().padStart(2, '0');
    const year = targetDate.getFullYear();

    if (language === 'ua') {
      return `${day}.${month}.${year}`;
    } else {
      return `${month}/${day}/${year}`;
    }
  };

  const getWeekRange = () => {
    const monday = new Date();
    const currentDayOfWeek = monday.getDay(); // 0 = Sunday, 1 = Monday, etc.

    // Convert to Monday-based index (0 = Monday, 6 = Sunday)
    const currentMondayIndex = currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1;

    // Calculate days to add to reach Monday of the target week
    const daysToMonday = -currentMondayIndex + (weekOffset * 7);

    monday.setDate(monday.getDate() + daysToMonday);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    const format = (d: Date) => {
      const day = d.getDate().toString().padStart(2, '0');
      const month = (d.getMonth() + 1).toString().padStart(2, '0');
      const year = d.getFullYear();
      return language === 'ua' ? `${day}.${month}.${year}` : `${month}/${day}/${year}`;
    };

    return `${format(monday)} - ${format(sunday)}`;
  };

  const handleBookSlot = (dayIndex: number, time: string) => {
    const dayName = formatDayName(days[dayIndex]);
    const date = getFormattedDate(dayIndex, weekOffset);

    // Check if the selected slot is in the past
    const targetDate = calculateTargetDate(dayIndex, weekOffset);
    const selectedDate = new Date(targetDate);

    // Set the time for the slot
    const [hours, minutes] = time.split(':').map(Number);
    selectedDate.setHours(hours, minutes, 0, 0);

    // Check if the slot is in the past (with 1 hour buffer for safety)
    const now = new Date();
    const bookingDeadline = new Date(now.getTime() + 60 * 60 * 1000); // 1 hour from now

    if (selectedDate <= bookingDeadline) {
      addNotification(
        language === 'ua'
          ? '❌ Неможливо забронювати час, що вже минув'
          : '❌ Cannot book a time that has already passed',
        'error'
      );
      return;
    }

    setBookingConfirmation({
      isOpen: true,
      day: dayName,
      time: time,
      date: date,
      notes: '',
    });
  };

  const confirmBooking = async () => {
    if (!bookingConfirmation || !user) return;

    try {
      // Prepare appointment data
      const appointmentData = {
        business_id: masterId, // masterId represents the business
        client_id: user.id, // Current authenticated user as client
        employee_id: null, // No specific employee selected
        service_id: null, // No specific service selected
        appointment_date: `${bookingConfirmation.date} ${bookingConfirmation.time}`, // Combine date and time
        duration_minutes: 60, // Default duration
        status: "pending",
        notes: bookingConfirmation.notes || `Без приміток`,
      };

      let response;
      let successMessage;

      if (isReschedule && appointmentId) {
        // Update existing appointment for reschedule
        response = await fetch(`/api/v1/appointments/${appointmentId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          },
          body: JSON.stringify(appointmentData),
        });

        successMessage = language === 'ua'
          ? `✅ Запис успішно перенесено! Майстер: ${masterName}, ${bookingConfirmation.date}, ${bookingConfirmation.time}`
          : `✅ Appointment rescheduled! Master: ${masterName}, ${bookingConfirmation.date}, ${bookingConfirmation.time}`;
      } else {
        // Create new appointment
        response = await fetch('/api/v1/appointments', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          },
          body: JSON.stringify(appointmentData),
        });

        successMessage = language === 'ua'
          ? `✅ Запис успішно створено! Майстер: ${masterName}, ${bookingConfirmation.date}, ${bookingConfirmation.time}`
          : `✅ Booking confirmed! Master: ${masterName}, ${bookingConfirmation.date}, ${bookingConfirmation.time}`;
      }

      if (!response.ok) {
        // Handle specific backend validation errors
        if (response.status === 400) {
          const errorData = await response.json().catch(() => ({ error: 'Validation error' }));
          if (errorData.details && errorData.details.includes('appointment date cannot be in the past')) {
            addNotification(
              language === 'ua'
                ? '❌ Неможливо забронювати час, що вже минув'
                : '❌ Cannot book a time that has already passed',
              'error'
            );
            setBookingConfirmation(null);
            return;
          }
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log(isReschedule ? 'Appointment rescheduled:' : 'Appointment created:', result);

      // Update the working hours to mark the slot as booked
      if (workingHours) {
        const dayIndex = days.findIndex(day =>
          formatDayName(day) === bookingConfirmation.day
        );

        if (dayIndex !== -1) {
          const updatedWorkingHours = { ...workingHours };
          const dayKey = days[dayIndex] as keyof WorkingHours;

          updatedWorkingHours[dayKey] = {
            ...updatedWorkingHours[dayKey],
            slots: updatedWorkingHours[dayKey].slots.map(slot =>
              slot.time === bookingConfirmation.time
                ? { ...slot, status: 'unavailable' }
                : slot
            )
          };

          setWorkingHours(updatedWorkingHours);
        }
      }

      // Close confirmation and show success message
      setBookingConfirmation(null);
      addNotification(successMessage, 'success');
      
      // Call callback to refresh appointments if it's a reschedule
      if (isReschedule && onRescheduleSuccess) {
        onRescheduleSuccess();
      }
      
      // Close modal after successful operation
      onClose();
    } catch (error) {
      console.error(isReschedule ? 'Error rescheduling appointment:' : 'Error creating appointment:', error);
      addNotification(
        language === 'ua'
          ? (isReschedule ? '❌ Помилка при перенесенні запису' : '❌ Помилка при створенні запису')
          : (isReschedule ? '❌ Error rescheduling appointment' : '❌ Error creating appointment'),
        'error'
      );
    }
  };

  const cancelBooking = () => {
    setBookingConfirmation(null);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="working-hours-modal">
        {/* <h4 className="modal-title">
          {language === 'ua' ? 'Години роботи' : 'Working Hours'} - {masterName}
        </h4> */}

        {loading && (
          <div className="loading-state">
            <p>{language === 'ua' ? 'Завантаження...' : 'Loading...'}</p>
          </div>
        )}

        {error && (
          <div className="error-state">
            <p>{error}</p>
          </div>
        )}

        {workingHours && !loading && !error && (
          <div className="working-hours-content">
            {/* Week Calendar Header */}
            <div className="week-calendar">
              <IconButton onClick={() => setWeekOffset(prev => prev - 1)} color="primary">
                <ArrowBack />
              </IconButton>
              <span className="week-range">{getWeekRange()}</span>
              <IconButton onClick={() => setWeekOffset(prev => prev + 1)} color="primary">
                <ArrowForward />
              </IconButton>
            </div>

            {/* Mobile Navigation */}
            <div className="mobile-navigation">
              <IconButton onClick={goToPreviousDay} color="primary">
                <ArrowBack />
              </IconButton>
              <div className="current-day">
                <div className="day-name">{formatDayName(days[currentDayIndex])}</div>
                <div className="day-date">{getFormattedDate(currentDayIndex, weekOffset)}</div>
              </div>
              <IconButton onClick={goToNextDay} color="primary">
                <ArrowForward />
              </IconButton>
            </div>

            {/* Mobile Single Day View */}
            <div className="mobile-day-view">
              <div className="day-slots-column">
                <div className="slots-list">
                  {workingHours[days[currentDayIndex] as keyof WorkingHours].closed ? (
                    <div className="slot-item closed">
                      {language === 'ua' ? 'Вихідний' : 'Closed'}
                    </div>
                  ) : (
                    workingHours[days[currentDayIndex] as keyof WorkingHours].slots.map((slot, index) => (
                      <div
                        key={index}
                        className={`slot-item ${slot.status} ${slot.status === 'available' ? 'clickable' : ''}`}
                        onClick={slot.status === 'available' ? () => handleBookSlot(currentDayIndex, slot.time) : undefined}
                      >
                        <span className="slot-time">{slot.time}</span>
                        <span className="slot-status">
                          {slot.status === 'available'
                            ? (language === 'ua' ? 'вільно' : 'available')
                            : slot.status === 'unavailable'
                            ? (language === 'ua' ? 'зайнято' : 'booked')
                            : (language === 'ua' ? 'закрито' : 'closed')
                          }
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Desktop Single Row View */}
            <div className="desktop-single-row">
              <div className="time-slots-grid desktop-grid">
                {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day, index) => {
                  const dayData = workingHours[day as keyof WorkingHours];
                  const isCurrentDay = isToday(index, weekOffset);
                  return (
                    <div key={day} className={`day-slots-column ${isCurrentDay ? 'current-day-highlight' : ''}`}>
                      <div className="day-title">
                        <div className="day-name">{formatDayName(day)}</div>
                        <div className="day-date">{getFormattedDate(index, weekOffset)}</div>
                      </div>
                      <div className="slots-list">
                        {dayData.closed ? (                          <div className="slot-item closed">
                            {language === 'ua' ? 'Вихідний' : 'Closed'}
                          </div>
                        ) : (
                          dayData.slots.map((slot, slotIndex) => (
                            <div
                              key={slotIndex}
                              className={`slot-item ${slot.status} ${slot.status === 'available' ? 'clickable' : ''}`}
                              onClick={slot.status === 'available' ? () => handleBookSlot(index, slot.time) : undefined}
                            >
                              <span className="slot-time">{slot.time}</span>
                              <span className="slot-status">
                                {slot.status === 'available'
                                  ? (language === 'ua' ? 'вільно' : 'available')
                                  : slot.status === 'unavailable'
                                  ? (language === 'ua' ? 'зайнято' : 'booked')
                                  : (language === 'ua' ? 'закрито' : 'closed')
                                }
                              </span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Legend */}
            <div className="schedule-legend">
              <div className="legend-item">
                <span className="legend-color available"></span>
                <span>{language === 'ua' ? 'Вільно' : 'Available'}</span>
              </div>
              <div className="legend-item">
                <span className="legend-color break"></span>
                <span>{language === 'ua' ? 'Закрито' : 'Closed'}</span>
              </div>
              <div className="legend-item">
                <span className="legend-color unavailable"></span>
                <span>{language === 'ua' ? 'Зайнято' : 'Booked'}</span>
              </div>
            </div>
          </div>
        )}

        {/* Booking Confirmation Dialog */}
        {bookingConfirmation?.isOpen && (
          <div className="booking-confirmation-overlay">
            <div className="booking-confirmation-modal">
              <h3>{language === 'ua' ? 'Підтвердження запису' : 'Booking Confirmation'}</h3>
              <div className="booking-details">
                <p><strong>{language === 'ua' ? 'Майстер:' : 'Master:'}</strong> {masterName}</p>
                <p><strong>{language === 'ua' ? 'Дата:' : 'Date:'}</strong> {bookingConfirmation.date}</p>
                <p><strong>{language === 'ua' ? 'Час:' : 'Time:'}</strong> {bookingConfirmation.time}</p>
                <p><strong>{language === 'ua' ? 'День:' : 'Day:'}</strong> {bookingConfirmation.day}</p>
                <div className="notes-section">
                  <p><strong>{language === 'ua' ? 'Примітки:' : 'Notes:'}</strong></p>
                  <textarea
                    className="notes-textarea"
                    value={bookingConfirmation.notes || ''}
                    onChange={(e) => setBookingConfirmation(prev => prev ? {...prev, notes: e.target.value} : null)}
                    placeholder={language === 'ua' ? 'Введіть примітки до запису...' : 'Enter notes for the appointment...'}
                    rows={3}
                  />
                </div>
              </div>
              <div className="booking-actions">
                <button
                  className="btn-confirm"
                  onClick={confirmBooking}
                >
                  {language === 'ua' ? 'Підтвердити' : 'Confirm'}
                </button>
                <button
                  className="btn-cancel"
                  onClick={cancelBooking}
                >
                  {language === 'ua' ? 'Скасувати' : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="modal-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
          >
            {language === 'ua' ? 'Закрити' : 'Close'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default WorkingHoursModal;
